import { AccountMilestoneStatus, MilestoneType } from 'types';

import { BillingBlockersModal } from '../BillingBlockersModal';
import { DirectPayOptInOutModal } from '../DirectPayOptInOutModal';
import { EnrollDomesticContentModal } from '../EnrollDomesticContentModal';
import type { AccountModalType } from '../hooks/AccountDetailsContext';
import { IncentivesHistoryModal } from '../IncentivesHistoryModal';
import { AccountPaymentMethod } from '../modals/AccountPaymentMethod';
import { AdjustMonthlyPaymentCap } from '../modals/AdjustMonthlyPaymentCap';
import { OneTimePaymentModal } from '../modals/OneTimePaymentModal';
import { ReCheckSalesRepModal } from '../modals/ReCheckSalesRepModal';
import { TrancheHistoryModal } from '../TrancheHistoryModal';

interface AccountDetailsModalsProps {
  activeModal: AccountModalType;
  closeModal: () => void;
  id: string;
  account: any;
  directPayDetails?: any;
  directPayMaterialVendorAliasOnAccount: any;
  orgDirectPaySettings: any;
  paymentMethod: any[];
  currentMonthlyPaymentCap: any;
  financingData: any;
  hasConcordAccountBeenCreated: boolean;
  shouldVerifySalesRep: boolean;
  isSalesRepInfoLoading: boolean;
  salesRepProviderUser: any;
  hasActiveQuote: boolean;
}

export function AccountDetailsModals({
  activeModal,
  closeModal,
  id,
  account,
  directPayDetails,
  directPayMaterialVendorAliasOnAccount,
  orgDirectPaySettings,
  paymentMethod,
  currentMonthlyPaymentCap,
  financingData,
  hasConcordAccountBeenCreated,
  shouldVerifySalesRep,
  isSalesRepInfoLoading,
  salesRepProviderUser,
  hasActiveQuote,
}: AccountDetailsModalsProps) {
  const isNtpComplete =
    account?.milestones?.some(
      (milestone: { type: string; completed: boolean }) =>
        milestone.type === MilestoneType.noticeToProceed && milestone.completed,
    ) ?? false;

  const isInstallPending =
    account?.milestones?.find(
      (milestone: { type: string; status: string }) => milestone.type === MilestoneType.install,
    )?.status === AccountMilestoneStatus.pending;

  const hasShipmentConfirmation = Boolean(directPayDetails?.shipConfirmationDate);
  const hasFinalizationInvoice = Boolean(directPayDetails?.invoiceFinalizationDate);

  return (
    <>
      <DirectPayOptInOutModal
        isModalOpen={activeModal === 'directPayOptOut'}
        handleCloseModal={closeModal}
        type="optOut"
        accountId={id}
        directPayMaterialVendorAliasOnAccount={directPayMaterialVendorAliasOnAccount}
        orgDirectPaySettings={orgDirectPaySettings}
        hasShipmentConfirmation={hasShipmentConfirmation}
        hasFinalizationInvoice={hasFinalizationInvoice}
      />
      <DirectPayOptInOutModal
        isModalOpen={activeModal === 'directPayOptIn'}
        handleCloseModal={closeModal}
        type="optIn"
        accountId={id}
        directPayMaterialVendorAliasOnAccount={directPayMaterialVendorAliasOnAccount}
        orgDirectPaySettings={orgDirectPaySettings}
        isNtpComplete={isNtpComplete}
        isInstallPending={isInstallPending}
        hasActiveQuote={hasActiveQuote}
      />
      <EnrollDomesticContentModal
        isModalOpen={activeModal === 'enrollDomesticContent'}
        handleCloseModal={closeModal}
        accountId={id}
      />
      <AccountPaymentMethod
        isModalOpen={activeModal === 'ach'}
        closeModal={closeModal}
        paymentMethods={paymentMethod}
        accountHolderName={account?.primaryApplicantName ?? ''}
      />
      <AdjustMonthlyPaymentCap
        isModalOpen={activeModal === 'adjustPaymentCap'}
        closeModal={closeModal}
        currentMonthlyPaymentCap={currentMonthlyPaymentCap}
      />
      <OneTimePaymentModal
        isModalOpen={activeModal === 'oneTimePayment'}
        closeModal={closeModal}
        defaultAmount={financingData?.totalDue}
        amountPastDue={financingData?.amountPastDue}
        hasConcordAccountBeenCreated={hasConcordAccountBeenCreated}
      />
      {shouldVerifySalesRep && !isSalesRepInfoLoading && salesRepProviderUser && (
        <ReCheckSalesRepModal
          isModalOpen={activeModal === 'reCheck'}
          closeModal={closeModal}
          salesRepAccountData={salesRepProviderUser}
          salesRepReCheckStatus={account?.salesRepVerification?.status}
        />
      )}
      <BillingBlockersModal isOpen={activeModal === 'billingBlock'} onDismiss={closeModal} account={account} />
      <TrancheHistoryModal isOpen={activeModal === 'trancheHistory'} onDismiss={closeModal} account={account} />
      <IncentivesHistoryModal
        isOpen={activeModal === 'incentivesHistory'}
        onDismiss={closeModal}
        account={account}
      />
    </>
  );
}
